import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { ProfileMenuClickedEvent } from '../../models/events/profileClickedEvent';

@Component({
    tag: 'portal-profile-menu-item',
    styleUrl: 'portal-profile-menu-item.scss',
    shadow: true,
})
export class PortalProfileMenuItem {
    @Element() el: HTMLPortalProfileMenuItemElement;

    /**
     * value of the menu
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() value: string;

    /**
     * value of the menu
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() text: string;

    /**
     * subtext of the menu
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() subtext: string;

    /**
     * icon of the list item
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() icon?: string;

    /**
     * active item of the list
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() active?: boolean;

    /**
     * is item of the list selectable by click or space or enter
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() selectable?: boolean;

    /**
     * is item of the list noninteractive to click or space or enter
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() noninteractive?: boolean;

    /**
     * active item of the list
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() submenu?: boolean;

    /**
    * Theme override to bypass store state
    * @memberof HTMLPortalProfileMenuElement
    */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * href of the menu item
     * @memberof HTMLPortalProfileMenuElement
     */
    @Prop() href: string = '';

    /**
    * Trigger an custom event for a settings item clicked
    * @event HTMLPortalProfileMenuElement
    */
    @Event({
        eventName: 'profileMenuItemClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) settingClick: EventEmitter<ProfileMenuClickedEvent>;

    render() {
        return <Host>
            <a
                href={this.href}
                aria-label={`${this.el.ariaLabel ?? this.el.id}`}
                id={`profile-menu-${this.el.id}`}
                data-cy={'profile-menu-' + this.el.id}
                class='pt-profile-menu'
                onKeyDown={this.handleKeyDown}
                onClick={this.handleClick}
                title={this.text}>
                <div data-test={`profile-menu-${this.el.id}`} class='content'>
                    {
                        this.selectable &&
                       this.active &&
                       <portal-icon
                           data-cy={'selected-icon-' + this.el.id}
                           size='24px'
                           class="active-icon"
                           color="var(--color-foreground-highlight)"
                           name="check" />
                    }
                    <div class={`text ${(this.selectable && !this.active) && 'selectable'} ${this.subtext && 'expanded'}`}>
                        {this.text && (
                            <div
                                class={`heading ${(this.selectable && this.active) ? 'selectable' : ''}`}
                                data-test={`profile-menu-${this.el.id}`}>
                                {this.text}
                            </div>
                        )}
                        {this.subtext && (
                            <div
                                id={`profile-menu-subtext-${this.el.id}`}
                                data-test={`profile-menu-subtext-${this.el.id}`}>
                                {this.subtext}
                            </div>
                        )}
                    </div>
                    { this.submenu && <portal-icon class="submenu-icon" size='24px' name="chevron_right"></portal-icon> }
                </div>
            </a>
        </Host>;
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.settingClick.emit({
                id: this.el.id,
                value: this.value,
            });
        }
    };

    private handleClick = (event: MouseEvent) => {
        event.preventDefault();
        this.settingClick.emit({
            id: this.el.id,
            value: this.value,
        });
    };
}
